import { ExportTaskListResponse, ExportTaskResponse } from 'types/exports';
import { MarketingProfileFilterCriteria } from 'types/marketingProfiles';
import { objectToQueryParams } from 'utils/linkUtil';
import { portalAPI } from './portalAPI';

export interface StartExportRequest {
    filter?: string;
    sort?: string;
}

interface StartMarketingProfileExportRequest {
    id: number;
    criteria?: MarketingProfileFilterCriteria;
}

const APIExportsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: ['ExportTasksList', 'ExportTask'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            // Start timecard async export
            startTimecardExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/timecards/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start invoice async export
            startInvoiceExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/invoices/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start invoice CR (short) async export
            startInvoiceCRExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/invoices/export/cr/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start prepayment async export
            startPrepaymentExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/invoices/export/prepayments/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start party async export
            startPartyExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/parties/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            startMarketingProfileExport: builder.mutation<ExportTaskResponse, StartMarketingProfileExportRequest>({
                query: ({ id, criteria }) => ({
                    url: `/v1/marketing-profiles/${id}/export/async`,
                    method: 'POST',
                    body: criteria,
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start matter async export
            startMatterExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/matters/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start expense async export
            startExpenseExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/expenses/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start correspondence async export
            startCorrespondenceExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/correspondence/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start BD effort async export
            // Start debriefing async export
            startDebriefingExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/debriefings/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            startEffortExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/efforts/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            startRankingsRefereeExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/rankings-referee-nominations/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Start experience records (matter-experience-records) async export
            startMatterExperienceRecordExport: builder.mutation<ExportTaskResponse, StartExportRequest>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/matter-experience-records/export/async${objectToQueryParams({ filter, sort })}`,
                    method: 'POST',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Get export task status
            getExportTask: builder.query<ExportTaskResponse, string>({
                query: (taskId) => `/api/queue-tasks/${taskId}`,
                providesTags: (_, __, taskId) => [{ type: 'ExportTask', id: taskId }],
            }),

            // Get all export tasks for current user
            getExportTasks: builder.query<ExportTaskListResponse[], void>({
                query: () => '/api/queue-tasks',
                providesTags: ['ExportTasksList'],
            }),

            // Delete export task
            deleteExportTask: builder.mutation<void, string>({
                query: (taskId) => ({
                    url: `/api/queue-tasks/${taskId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),

            // Delete all completed/failed tasks
            deleteCompletedTasks: builder.mutation<void, void>({
                query: () => ({
                    url: '/api/queue-tasks',
                    method: 'DELETE',
                }),
                invalidatesTags: ['ExportTasksList'],
            }),
        }),
    });

export const {
    useStartTimecardExportMutation,
    useStartInvoiceExportMutation,
    useStartInvoiceCRExportMutation,
    useStartPrepaymentExportMutation,
    useStartPartyExportMutation,
    useStartMarketingProfileExportMutation,
    useStartMatterExportMutation,
    useStartExpenseExportMutation,
    useStartCorrespondenceExportMutation,
    useStartDebriefingExportMutation,
    useStartEffortExportMutation,
    useStartRankingsRefereeExportMutation,
    useStartMatterExperienceRecordExportMutation,
    useGetExportTaskQuery,
    useGetExportTasksQuery,
    useDeleteExportTaskMutation,
    useDeleteCompletedTasksMutation,
} = APIExportsSlice;
